<template>
  <div class="dt-range-picker" v-clickoutside="handleClose">
    <el-popover
      v-model="visible"
      placement="bottom-start"
      trigger="manual"
      popper-class="dt-range-picker-popover"
      width="420"
    >
      <div class="dt-range-picker-panel">
        <!-- 头部：年月导航 + 当前结束时间显示 -->
        <div class="dt-range-header">
          <div class="dt-range-nav">
            <i class="el-icon-d-arrow-left" @click="prevYear"></i>
            <i class="el-icon-arrow-left" @click="prevMonth"></i>
            <span class="dt-range-title">{{ viewYear }}年{{ viewMonth }}月</span>
            <i class="el-icon-arrow-right" @click="nextMonth"></i>
            <i class="el-icon-d-arrow-right" @click="nextYear"></i>
          </div>
          <div class="dt-range-end-time">{{ formatDisplay(endDate) }}</div>
        </div>
        <!-- 日历 + 时间 -->
        <div class="dt-range-body">
          <div class="dt-range-calendar">
            <div class="dt-range-weekdays">
              <span v-for="w in weekdays" :key="w">{{ w }}</span>
            </div>
            <div class="dt-range-days">
              <span
                v-for="(cell, idx) in calendarDays"
                :key="idx"
                :class="[
                  'dt-range-day',
                  {
                    other: cell.other,
                    inrange: cell.inRange,
                    start: cell.isStart,
                    end: cell.isEnd,
                    current: cell.isCurrent,
                  },
                ]"
                @click="pickDate(cell)"
              >
                {{ cell.day }}
              </span>
            </div>
          </div>
          <div class="dt-range-time">
            <div class="dt-time-label">开始时间</div>
            <div class="dt-time-row">
              <el-select :value="startHourStr" size="mini" class="dt-time-select" @change="onStartHourChange">
                <el-option v-for="h in 24" :key="'s'+h" :label="pad2(h - 1)" :value="pad2(h - 1)"></el-option>
              </el-select>
              <span class="dt-time-sep">:</span>
              <el-select :value="startMinuteStr" size="mini" class="dt-time-select" @change="onStartMinuteChange">
                <el-option v-for="m in 60" :key="'sm'+m" :label="pad2(m - 1)" :value="pad2(m - 1)"></el-option>
              </el-select>
            </div>
            <div class="dt-time-label">结束时间</div>
            <div class="dt-time-row">
              <el-select :value="endHourStr" size="mini" class="dt-time-select" @change="onEndHourChange">
                <el-option v-for="h in 24" :key="'e'+h" :label="pad2(h - 1)" :value="pad2(h - 1)"></el-option>
              </el-select>
              <span class="dt-time-sep">:</span>
              <el-select :value="endMinuteStr" size="mini" class="dt-time-select" @change="onEndMinuteChange">
                <el-option v-for="m in 60" :key="'em'+m" :label="pad2(m - 1)" :value="pad2(m - 1)"></el-option>
              </el-select>
            </div>
          </div>
        </div>
        <!-- 预设快捷 -->
        <div class="dt-range-presets">
          <el-button v-for="p in presets" :key="p.key" size="mini" class="dt-preset-btn" @click="applyPreset(p.key)">
            {{ p.label }}
          </el-button>
        </div>
        <div class="dt-range-footer">
          <el-button type="primary" size="small" @click="confirm">确定</el-button>
        </div>
      </div>
      <div slot="reference" class="dt-range-reference">
        <input
          type="text"
          class="dt-range-input"
          :value="displayText"
          readonly
          placeholder="请选择时间范围"
          @click.prevent.stop="openPopover"
        />
        <i class="el-icon-date dt-range-icon" @click.prevent.stop="openPopover"></i>
      </div>
    </el-popover>
  </div>
</template>

<script>
const weekdays = ["一", "二", "三", "四", "五", "六", "日"];

function toDate(y, m, d, h, min) {
  const month = m - 1;
  const date = new Date(y, month, d, h ?? 0, min ?? 0, 0, 0);
  return date;
}

function formatYMDHM(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day} ${h}:${min}`;
}

function dateOnly(d) {
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

export default {
  name: "DateTimeRangePicker",
  directives: {
    clickoutside: {
      bind(el, binding) {
        function documentHandler(e) {
          if (el.contains(e.target)) return;
          const popover = document.querySelector(".dt-range-picker-popover");
          if (popover && popover.contains(e.target)) return;
          if (binding.expression) binding.value(e);
        }
        el.__vueClickOutside__ = documentHandler;
        document.addEventListener("click", documentHandler);
      },
      unbind(el) {
        document.removeEventListener("click", el.__vueClickOutside__);
        delete el.__vueClickOutside__;
      },
    },
  },
  props: {
    value: {
      type: Array,
      default: () => null,
    },
  },
  data() {
    const now = new Date();
    const todayStart = toDate(now.getFullYear(), now.getMonth() + 1, now.getDate(), 0, 0);
    const todayEnd = toDate(now.getFullYear(), now.getMonth() + 1, now.getDate(), 23, 59);
    return {
      visible: false,
      weekdays,
      viewYear: now.getFullYear(),
      viewMonth: now.getMonth() + 1,
      startDate: todayStart,
      endDate: todayEnd,
      startHour: 0,
      startMinute: 0,
      endHour: 23,
      endMinute: 59,
      pickingStart: true,
      presets: [
        { key: "today", label: "今天" },
        { key: "yesterday", label: "昨天" },
        { key: "thisWeek", label: "本周" },
        { key: "past7", label: "过去7天" },
        { key: "past15", label: "过去15天" },
        { key: "thisMonth", label: "本月" },
        { key: "past30", label: "过去30天" },
        { key: "past90", label: "过去90天" },
        { key: "past180", label: "过去180天" },
        { key: "past365", label: "过去365天" },
      ],
    };
  },
  computed: {
    startHourStr() {
      return this.pad2(this.startHour);
    },
    startMinuteStr() {
      return this.pad2(this.startMinute);
    },
    endHourStr() {
      return this.pad2(this.endHour);
    },
    endMinuteStr() {
      return this.pad2(this.endMinute);
    },
    displayText() {
      if (!this.startDate || !this.endDate) return "";
      return `${formatYMDHM(this.startDate)} → ${formatYMDHM(this.endDate)}`;
    },
    calendarDays() {
      const y = this.viewYear;
      const m = this.viewMonth;
      const first = new Date(y, m - 1, 1);
      const firstWeekday = first.getDay();
      const startOffset = firstWeekday === 0 ? 6 : firstWeekday - 1;
      const daysInMonth = new Date(y, m, 0).getDate();
      const prevMonth = m === 1 ? 12 : m - 1;
      const prevYear = m === 1 ? y - 1 : y;
      const daysInPrev = new Date(prevYear, prevMonth, 0).getDate();
      const cells = [];
      const startVal = this.startDate ? dateOnly(this.startDate) : null;
      const endVal = this.endDate ? dateOnly(this.endDate) : null;
      const todayVal = dateOnly(new Date());

      for (let i = 0; i < startOffset; i++) {
        const d = daysInPrev - startOffset + 1 + i;
        const cellDate = toDate(prevYear, prevMonth, d);
        const val = dateOnly(cellDate);
        cells.push({
          day: d,
          date: cellDate,
          other: true,
          inRange: startVal && endVal && val >= startVal && val <= endVal,
          isStart: startVal && val === startVal,
          isEnd: endVal && val === endVal,
          isCurrent: val === todayVal,
        });
      }
      for (let d = 1; d <= daysInMonth; d++) {
        const cellDate = toDate(y, m, d);
        const val = dateOnly(cellDate);
        cells.push({
          day: d,
          date: cellDate,
          other: false,
          inRange: startVal && endVal && val >= startVal && val <= endVal,
          isStart: startVal && val === startVal,
          isEnd: endVal && val === endVal,
          isCurrent: val === todayVal,
        });
      }
      const remain = 42 - cells.length;
      for (let i = 0; i < remain; i++) {
        const d = i + 1;
        const nextMonth = m === 12 ? 1 : m + 1;
        const nextYear = m === 12 ? y + 1 : y;
        const cellDate = toDate(nextYear, nextMonth, d);
        const val = dateOnly(cellDate);
        cells.push({
          day: d,
          date: cellDate,
          other: true,
          inRange: startVal && endVal && val >= startVal && val <= endVal,
          isStart: startVal && val === startVal,
          isEnd: endVal && val === endVal,
          isCurrent: val === todayVal,
        });
      }
      return cells;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (val && val.length === 2 && val[0] && val[1]) {
          this.startDate = this.parseInput(val[0]);
          this.endDate = this.parseInput(val[1]);
          if (this.startDate) {
            this.startHour = this.startDate.getHours();
            this.startMinute = this.startDate.getMinutes();
            this.viewYear = this.startDate.getFullYear();
            this.viewMonth = this.startDate.getMonth() + 1;
          }
          if (this.endDate) {
            this.endHour = this.endDate.getHours();
            this.endMinute = this.endDate.getMinutes();
          }
        }
      },
    },
  },
  methods: {
    pad2(n) {
      return String(n).padStart(2, "0");
    },
    onStartHourChange(val) {
      this.startHour = parseInt(val, 10) || 0;
      this.applyStartTime();
    },
    onStartMinuteChange(val) {
      this.startMinute = parseInt(val, 10) || 0;
      this.applyStartTime();
    },
    onEndHourChange(val) {
      this.endHour = parseInt(val, 10) || 0;
      this.applyEndTime();
    },
    onEndMinuteChange(val) {
      this.endMinute = parseInt(val, 10) || 0;
      this.applyEndTime();
    },
    openPopover() {
      this.visible = true;
    },
    handleClose() {
      this.visible = false;
    },
    formatDisplay(d) {
      return formatYMDHM(d);
    },
    parseInput(str) {
      if (!str) return null;
      const part = str.split(" ");
      const [y, m, day] = (part[0] || "").split("-").map(Number);
      let h = 0,
        min = 0;
      if (part[1]) {
        const [hh, mm] = part[1].split(":").map(Number);
        h = isNaN(hh) ? 0 : hh;
        min = isNaN(mm) ? 0 : mm;
      }
      if (isNaN(y) || isNaN(m) || isNaN(day)) return null;
      return toDate(y, m, day, h, min);
    },
    prevYear() {
      this.viewYear--;
    },
    nextYear() {
      this.viewYear++;
    },
    prevMonth() {
      if (this.viewMonth === 1) {
        this.viewMonth = 12;
        this.viewYear--;
      } else {
        this.viewMonth--;
      }
    },
    nextMonth() {
      if (this.viewMonth === 12) {
        this.viewMonth = 1;
        this.viewYear++;
      } else {
        this.viewMonth++;
      }
    },
    pickDate(cell) {
      const d = cell.date;
      if (this.pickingStart) {
        this.startDate = toDate(d.getFullYear(), d.getMonth() + 1, d.getDate(), this.startHour, this.startMinute);
        this.endDate = toDate(d.getFullYear(), d.getMonth() + 1, d.getDate(), this.endHour, this.endMinute);
        this.pickingStart = false;
        if (dateOnly(this.startDate) > dateOnly(this.endDate)) {
          this.endDate = toDate(d.getFullYear(), d.getMonth() + 1, d.getDate(), 23, 59);
          this.endHour = 23;
          this.endMinute = 59;
        }
      } else {
        let start = this.startDate;
        let end = toDate(d.getFullYear(), d.getMonth() + 1, d.getDate(), this.endHour, this.endMinute);
        if (dateOnly(end) < dateOnly(start)) {
          [start, end] = [end, start];
          this.startDate = toDate(start.getFullYear(), start.getMonth() + 1, start.getDate(), this.startHour, this.startMinute);
          this.endDate = toDate(end.getFullYear(), end.getMonth() + 1, end.getDate(), 23, 59);
          this.endHour = 23;
          this.endMinute = 59;
        } else {
          this.endDate = end;
          this.endHour = end.getHours();
          this.endMinute = end.getMinutes();
        }
        this.startDate = toDate(start.getFullYear(), start.getMonth() + 1, start.getDate(), this.startHour, this.startMinute);
        this.startHour = this.startDate.getHours();
        this.startMinute = this.startDate.getMinutes();
        this.pickingStart = true;
      }
    },
    applyStartTime() {
      if (!this.startDate) return;
      this.startDate = toDate(
        this.startDate.getFullYear(),
        this.startDate.getMonth() + 1,
        this.startDate.getDate(),
        this.startHour,
        this.startMinute
      );
    },
    applyEndTime() {
      if (!this.endDate) return;
      this.endDate = toDate(
        this.endDate.getFullYear(),
        this.endDate.getMonth() + 1,
        this.endDate.getDate(),
        this.endHour,
        this.endMinute
      );
    },
    applyPreset(key) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      let start, end;
      switch (key) {
        case "today":
          start = new Date(today);
          end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
          break;
        case "yesterday": {
          start = new Date(today);
          start.setDate(start.getDate() - 1);
          end = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 23, 59, 59, 0);
          break;
        }
        case "thisWeek": {
          const day = now.getDay() || 7;
          start = new Date(today);
          start.setDate(start.getDate() - day + 1);
          end = new Date(today);
          end.setDate(end.getDate() + (7 - day));
          end.setHours(23, 59, 59, 0);
          break;
        }
        case "past7":
          end = new Date(today);
          end.setHours(23, 59, 59, 0);
          start = new Date(end);
          start.setDate(start.getDate() - 6);
          start.setHours(0, 0, 0, 0);
          break;
        case "past15":
          end = new Date(today);
          end.setHours(23, 59, 59, 0);
          start = new Date(end);
          start.setDate(start.getDate() - 14);
          start.setHours(0, 0, 0, 0);
          break;
        case "thisMonth":
          start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
          end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
          break;
        case "past30":
          end = new Date(today);
          end.setHours(23, 59, 59, 0);
          start = new Date(end);
          start.setDate(start.getDate() - 29);
          start.setHours(0, 0, 0, 0);
          break;
        case "past90":
          end = new Date(today);
          end.setHours(23, 59, 59, 0);
          start = new Date(end);
          start.setDate(start.getDate() - 89);
          start.setHours(0, 0, 0, 0);
          break;
        case "past180":
          end = new Date(today);
          end.setHours(23, 59, 59, 0);
          start = new Date(end);
          start.setDate(start.getDate() - 179);
          start.setHours(0, 0, 0, 0);
          break;
        case "past365":
          end = new Date(today);
          end.setHours(23, 59, 59, 0);
          start = new Date(end);
          start.setDate(start.getDate() - 364);
          start.setHours(0, 0, 0, 0);
          break;
        default:
          return;
      }
      this.startDate = start;
      this.endDate = end;
      this.startHour = start.getHours();
      this.startMinute = start.getMinutes();
      this.endHour = end.getHours();
      this.endMinute = end.getMinutes();
      this.viewYear = end.getFullYear();
      this.viewMonth = end.getMonth() + 1;
    },
    confirm() {
      this.$emit("input", [formatYMDHM(this.startDate), formatYMDHM(this.endDate)]);
      this.$emit("change", [formatYMDHM(this.startDate), formatYMDHM(this.endDate)]);
      this.visible = false;
    },
  },
};
</script>

<style scoped lang="scss">
.dt-range-picker {
  display: inline-block;
}
.dt-range-reference {
  display: inline-flex;
  align-items: center;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  padding: 0 8px;
  min-width: 320px;
  cursor: pointer;
  &:hover {
    border-color: #c0c4cc;
  }
}
.dt-range-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 7px 0;
  font-size: 13px;
  color: #606266;
  background: transparent;
  cursor: pointer;
  &::placeholder {
    color: #c0c4cc;
  }
}
.dt-range-icon {
  color: #c0c4cc;
  font-size: 16px;
  margin-left: 8px;
}
</style>

<style lang="scss">
.dt-range-picker-popover {
  padding: 12px;
  .dt-range-picker-panel {
    .dt-range-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
    }
    .dt-range-nav {
      display: flex;
      align-items: center;
      gap: 4px;
      i {
        cursor: pointer;
        color: #606266;
        font-size: 14px;
        padding: 2px;
        &:hover {
          color: #409eff;
        }
      }
    }
    .dt-range-title {
      font-size: 14px;
      color: #303133;
      min-width: 80px;
      text-align: center;
    }
    .dt-range-end-time {
      font-size: 13px;
      color: #606266;
    }
    .dt-range-body {
      display: flex;
      gap: 16px;
      margin-bottom: 12px;
    }
    .dt-range-calendar {
      flex: 1;
      min-width: 0;
    }
    .dt-range-weekdays {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      text-align: center;
      font-size: 12px;
      color: #909399;
      margin-bottom: 4px;
    }
    .dt-range-days {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 2px;
      font-size: 13px;
    }
    .dt-range-day {
      text-align: center;
      padding: 6px 0;
      border-radius: 4px;
      cursor: pointer;
      &.other {
        color: #c0c4cc;
      }
      &.inrange {
        background: #ecf5ff;
        color: #409eff;
      }
      &.start,
      &.end {
        background: #409eff;
        color: #fff;
      }
      &.current {
        border: 1px solid #409eff;
      }
      &:hover:not(.other) {
        background: #f5f7fa;
      }
    }
    .dt-range-time {
      width: 90px;
      flex-shrink: 0;
    }
    .dt-time-label {
      font-size: 12px;
      color: #606266;
      margin-bottom: 4px;
    }
    .dt-time-row {
      display: flex;
      align-items: center;
      .dt-time-select {
        width: 44px;
      }
      .dt-time-sep {
        margin: 0 2px;
        color: #606266;
      }
    }
    .dt-time-hint {
      font-size: 11px;
      color: #909399;
      margin-top: 4px;
    }
    .dt-range-presets {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 12px;
      .dt-preset-btn {
        margin: 0;
      }
    }
    .dt-range-footer {
      text-align: right;
    }
  }
}
</style>
